import {useEffect} from 'react';
import {trpc} from '@common-web/lib/trpc';
import {Label} from '@common-web/components/ui/label';
import {RadioGroup, RadioGroupItem} from '@common-web/components/ui/radio-group';
import {Skeleton} from '@common-web/components/ui/skeleton';

/**
 * Role picker for the user-registration forms, fed by `roles.listAssignable`
 * so admin-created CUSTOM roles are offered alongside the built-in SYSTEM
 * ones. Scope-filtered server-side; tenant callers can only list
 * non-INTERNAL scopes.
 *
 * Custom roles are tenant-owned, so pass the target `clientId` (the client
 * the user is being created in) to include that tenant's custom roles. For
 * internal callers with no clientId, only the global SYSTEM catalog is
 * listed; tenant callers are pinned to their own client server-side.
 *
 * When the query 403s or returns nothing, the picker clears the form's
 * preselected default role (so the `!form.role` submit gate blocks) and
 * explains why — otherwise the form would silently submit a role the viewer
 * never saw.
 */
export function AssignableRolePicker({
  scope,
  clientId,
  idPrefix,
  value,
  onChange,
}: {
  scope: 'INTERNAL' | 'CLIENT' | 'GROUP';
  clientId?: string;
  idPrefix: string;
  value: string;
  onChange: (roleKey: string) => void;
}) {
  const {
    data: assignableRoles,
    isLoading,
    isError,
  } = trpc.internal.roles.listAssignable.useQuery(
    {scope, clientId: clientId || undefined},
    {retry: false},
  );

  const rolesUnavailable = isError || (!isLoading && (assignableRoles ?? []).length === 0);

  // Clear the selection when it isn't offerable: the roles failed to load, or
  // the loaded list doesn't contain it (e.g. the admin picked tenant A's
  // custom role and then switched the form to tenant B — without this the
  // radio group shows nothing selected while the stale key keeps the submit
  // button enabled, and the server rejects it).
  const valueNotOffered =
    !!value && !isLoading && !!assignableRoles && !assignableRoles.some((r) => r.key === value);

  useEffect(() => {
    if ((rolesUnavailable || valueNotOffered) && value) {
      onChange('');
    }
  }, [rolesUnavailable, valueNotOffered, value, onChange]);

  return (
    <div className='space-y-2'>
      <Label>Access Role</Label>
      {isLoading ? (
        <Skeleton className='h-10 w-full' />
      ) : rolesUnavailable ? (
        <p className='text-sm text-brand-gray'>
          No assignable roles are available — you may not have permission to assign roles for this
          user type. Contact an administrator.
        </p>
      ) : (
        <RadioGroup value={value} onValueChange={onChange}>
          {(assignableRoles ?? []).map((r) => (
            <div key={r.key} className='flex items-start space-x-2'>
              <RadioGroupItem value={r.key} id={`${idPrefix}-role-${r.key}`} className='mt-1' />
              <Label htmlFor={`${idPrefix}-role-${r.key}`} className='font-normal cursor-pointer'>
                <div>
                  <div className='font-medium'>{r.name}</div>
                  {r.description ? (
                    <div className='text-xs text-brand-gray'>{r.description}</div>
                  ) : null}
                </div>
              </Label>
            </div>
          ))}
        </RadioGroup>
      )}
    </div>
  );
}
